import { useEffect } from "react";
import { registerTelemetrySink } from "@onetasklist/shared";
import { useAuth } from "@contexts/AuthContext";
import { useConfig } from "@contexts/ConfigContext";
import {
  createApplicationInsightsSink,
  startApplicationInsights,
} from "@utils/applicationInsights";

/**
 * Sends shared code's telemetry to the region's Application Insights once the region has said
 * where that is. Tagged with the signed-in user's id so funnels and retention can be counted per
 * user. Stops sending on sign-out or a region change.
 */
export const useApplicationInsights = (): void => {
  const { config } = useConfig();
  const { user } = useAuth();
  const connectionString = config?.applicationInsightsConnectionString;
  const userId = user?.userId;

  useEffect(() => {
    if (!connectionString || !userId) return;

    let cancelled = false;
    let stop: (() => void) | undefined;

    startApplicationInsights(connectionString)
      .then((client) => {
        if (cancelled) return;
        client.setAuthenticatedUserContext(userId, undefined, false);
        client.trackPageView();
        registerTelemetrySink(createApplicationInsightsSink(client));
        stop = () => {
          registerTelemetrySink(null);
          client.clearAuthenticatedUserContext();
          client.flush();
        };
      })
      .catch(() => {
        // Telemetry must never break the app; without it shared code's calls stay no-ops.
      });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [connectionString, userId]);
};
